import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, Avatar, TextField,
  IconButton, CircularProgress
} from '@mui/material';
import { X, Check, Calendar } from 'lucide-react';
import moment from 'moment';
import { toastError, toastSuccess } from '../../utils';
import { API_URL } from '../../constants';
import StatusBadge from '../../components/StatusBadge';
import ActionButton from '../../components/ActionButton';
import ConfirmModal from '../Common/CommonModal';
import * as profileActions from '../Profile/actions';

const durationMap = { short_1hr: 60, short_1_5hr: 90, short_2hr: 120, short_2_5hr: 150, short_3hr: 180 };

const formatTimeframe = (leaveType, leaveTime) => {
  if (!leaveTime || !durationMap[leaveType]) return '';
  const [h, m] = leaveTime.split(':').map(Number);
  const startMin = h * 60 + m;
  const endMin = startMin + durationMap[leaveType];
  const fmt = (mins) => {
    let hr = Math.floor(mins / 60) % 24;
    const mn = mins % 60;
    const ampm = hr >= 12 ? 'PM' : 'AM';
    hr = hr % 12 || 12;
    return `${hr}:${String(mn).padStart(2, '0')} ${ampm}`;
  };
  return `${fmt(startMin)} - ${fmt(endMin)}`;
};

const leaveTypeLabels = {
  full_day: 'Full Day',
  half_day_morning: 'Morning',
  half_day_afternoon: 'Afternoon',
  short_1hr: '1 Hour',
  short_1_5hr: '1.5 Hours',
  short_2hr: '2 Hours',
  short_2_5hr: '2.5 Hours',
  short_3hr: '3 Hours',
};

export default function LeaveApprovalModal({ open, onClose, setLoader, onActionComplete }) {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [reviewNote, setReviewNote] = useState('');

  useEffect(() => {
    if (open) loadPendingLeaves();
  }, [open]);

  const loadPendingLeaves = () => {
    setLoading(true);
    profileActions.getPendingLeaveRequests()
      .then((res) => {
        setLoading(false);
        setLeaves(res.body || []);
      })
      .catch((err) => {
        setLoading(false);
        toastError(err?.message || 'Failed to load pending leave requests');
      });
  };

  const openConfirm = (leaveId, action) => {
    setSelectedLeaveId(leaveId);
    setConfirmAction(action);
    setReviewNote('');
    setConfirmOpen(true);
  };

  const onConfirmSubmit = () => {
    setConfirmOpen(false);
    setLoader(true);
    profileActions.reviewLeave({
      leaveId: selectedLeaveId,
      action: confirmAction,
      reviewNote: reviewNote.trim() || undefined,
    })
      .then((res) => {
        setLoader(false);
        toastSuccess(res.message || `Leave ${confirmAction} successfully`);
        loadPendingLeaves();
        if (onActionComplete) onActionComplete();
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.message || 'Failed to process leave request');
      });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1A1D27',
            border: '1px solid #2E3247',
            borderRadius: '12px',
            maxHeight: '80vh',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #2E3247',
          py: 1.5,
          px: 2.5,
        }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#F1F5F9' }}>
            Pending Leave Requests
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#64748B', p: 0.5 }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress size={24} sx={{ color: '#6C63FF' }} />
            </Box>
          ) : leaves.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} py={5}>
              <Calendar size={36} color="#2E3247" strokeWidth={1.5} />
              <Typography sx={{ color: '#64748B', fontSize: '0.85rem' }}>
                No pending leave requests
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
              {leaves.map((leave) => (
                <Box
                  key={leave.leaveId}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1.8,
                    borderRadius: '10px',
                    backgroundColor: '#1E2130',
                    border: '1px solid #2E3247',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: '#3E4257' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Avatar
                      src={leave.photograph ? `${API_URL}/${leave.photograph}` : undefined}
                      sx={{
                        width: 36,
                        height: 36,
                        backgroundColor: '#6C63FF',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}
                    >
                      {leave.name ? leave.name.charAt(0).toUpperCase() : '?'}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Typography sx={{ fontSize: '0.88rem', color: '#F1F5F9', fontWeight: 600 }}>
                          {leave.name}
                        </Typography>
                        <StatusBadge status={leave.status} />
                      </Box>
                      <Typography sx={{ fontSize: '0.68rem', color: '#64748B' }}>
                        {leave.email}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.92rem', color: '#F1F5F9', fontWeight: 700, flexShrink: 0 }}>
                      {moment(leave.leaveDate).format('DD MMM YYYY')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, ml: 0.5 }}>
                    <Box display="flex" gap={0.8} sx={{ width: '48%' }}>
                      <Typography sx={{ fontSize: '0.76rem', color: '#64748B', fontWeight: 600 }}>Type :</Typography>
                      <Typography sx={{ fontSize: '0.76rem', color: '#CBD5E1' }}>
                        {leave.leaveTime ? formatTimeframe(leave.leaveType, leave.leaveTime) : (leaveTypeLabels[leave.leaveType] || 'Full Day')}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={0.8} sx={{ width: '48%' }}>
                      <Typography sx={{ fontSize: '0.76rem', color: '#64748B', fontWeight: 600 }}>Applied :</Typography>
                      <Typography sx={{ fontSize: '0.76rem', color: '#CBD5E1' }}>
                        {moment(leave.createdAt).format('DD MMM YYYY')}
                      </Typography>
                    </Box>
                    {leave.reason && (
                      <Box display="flex" gap={0.8} sx={{ width: '100%' }}>
                        <Typography sx={{ fontSize: '0.76rem', color: '#64748B', fontWeight: 600, flexShrink: 0 }}>Reason :</Typography>
                        <Typography sx={{ fontSize: '0.76rem', color: '#CBD5E1' }}>
                          {leave.reason}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.8, justifyContent: 'flex-end', mt: 1.2 }}>
                    <ActionButton
                      variant="success"
                      onClick={() => openConfirm(leave.leaveId, 'approved')}
                      sx={{ fontSize: '0.72rem', px: 1.2, py: 0.2, minWidth: 0 }}
                    >
                      <Check size={13} style={{ marginRight: 3 }} />
                      Approve
                    </ActionButton>
                    <ActionButton
                      variant="danger"
                      onClick={() => openConfirm(leave.leaveId, 'rejected')}
                      sx={{ fontSize: '0.72rem', px: 1.2, py: 0.2, minWidth: 0 }}
                    >
                      <X size={13} style={{ marginRight: 3 }} />
                      Reject
                    </ActionButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmModal
        open={confirmOpen}
        title={confirmAction === 'approved' ? 'Approve Leave' : 'Reject Leave'}
        maxWidth="sm"
        fullWidth
        message={
          <Box>
            <Typography sx={{ fontSize: '0.88rem', mb: 1.5 }}>
              {confirmAction === 'approved'
                ? 'Are you sure you want to approve this leave request?'
                : 'Are you sure you want to reject this leave request?'}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Add a note (optional)..."
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              size="small"
            />
          </Box>
        }
        onSubmitConfirm={onConfirmSubmit}
        handleClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
